<template>
  <div class="admin-view">
    <div v-if="checkingSession" class="loading-shell" aria-label="Chargement">
      <div class="loading-spinner" aria-hidden="true"></div>
    </div>
    <div v-else-if="!isAuthenticated" class="login-shell">
      <div v-if="loggingIn" class="loading-shell" aria-label="Connexion en cours">
        <div class="loading-spinner" aria-hidden="true"></div>
      </div>

      <form v-else class="login-card" @submit.prevent="login">
        <input
            v-model="password"
            type="password"
            autocomplete="current-password"
            placeholder="Mot de passe"
        >
        <button type="submit" :disabled="loggingIn" title="Se connecter � l'interface admin">Connexion</button>
        <p v-if="loginError" class="error-text">{{ loginError }}</p>
      </form>
    </div>

    <div v-else class="admin-shell">

      <p v-if="pageError" class="error-text page-error">{{ pageError }}</p>

      <transition-group name="portfolio-grid" tag="div" class="portfolio-grid">
        <AdminPortfolioCard
            v-for="item in items"
            :key="item.id"
            :dragging="draggingItemId === item.id"
            :item="item"
            @drag-start="onItemDragStart(item.id, $event)"
            @drag-enter="onItemDragOver(item.id, $event)"
            @drag-over="onItemDragOver(item.id, $event)"
            @drop="onItemDrop($event)"
            @drag-end="onItemDragEnd($event)"
            @delete="deleteItem(item.id)"
            @open="selectItem(item.id)"
        />
      </transition-group>

      <button
          class="icon-action icon-action--technologies"
          type="button"
          title="Gérer les technologies"
          aria-label="Gérer les technologies"
          @click="technologyManagerOpen = true"
      >
        <svg class="bi bi-cpu" viewBox="0 0 16 16" aria-hidden="true">
          <path d="M5 0a.5.5 0 0 1 .5.5V1h5V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v1h.5a.5.5 0 0 1 0 1H14v5h.5a.5.5 0 0 1 0 1H14v1a2 2 0 0 1-2 2h-1v.5a.5.5 0 0 1-1 0V15h-5v.5a.5.5 0 0 1-1 0V15H3a2 2 0 0 1-2-2v-1H.5a.5.5 0 0 1 0-1H1V5H.5a.5.5 0 0 1 0-1H1V3a2 2 0 0 1 2-2h1V.5A.5.5 0 0 1 5 0m-2 2a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1zm2 2h6v6H5z"/>
        </svg>
      </button>

      <button
          class="icon-action icon-action--create"
          type="button"
          title="Créer un nouvel item du portfolio"
          aria-label="Créer un nouvel item du portfolio"
          @click="createDraft"
      >
        <svg class="bi bi-plus-lg" viewBox="0 0 16 16" aria-hidden="true">
          <path fill-rule="evenodd" d="M8 2a.5.5 0 0 1 .5.5V7.5H13.5a.5.5 0 0 1 0 1H8.5V13.5a.5.5 0 0 1-1 0V8.5H2.5a.5.5 0 0 1 0-1H7.5V2.5A.5.5 0 0 1 8 2"/>
        </svg>
      </button>

      <AdminPortfolioModal
          :model-value="editorOpen"
          :item="draft"
          :all-technologies="technologies"
          :create-technology="(name) => createTechnology(name)"
          :delete-technology="(technologyId) => deleteTechnology(technologyId)"
          :creating-technology="creatingTechnology"
          :confirm-action="confirmAction"
          :set-status="setStatus"
          :clear-status="clearStatus"
          :saving="saving"
          :error-text="editorError"
          @update:modelValue="setEditorOpen"
          @save="saveItem"
      />

      <TechnologyManagerModal
          v-model="technologyManagerOpen"
          :technologies="technologies"
          :create-technology="(name) => createTechnology(name)"
          :update-technology="(technologyId, name) => updateTechnology(technologyId, name)"
          :delete-technology="(technologyId) => deleteTechnology(technologyId)"
          :creating="creatingTechnology"
      />

      <ConfirmDialog
          v-model="confirmState.visible"
          :title="confirmState.title"
          :message="confirmState.message"
          :confirm-label="confirmState.confirmLabel"
          :cancel-label="confirmState.cancelLabel"
          :danger="confirmState.danger"
          @confirm="resolveConfirm(true)"
          @cancel="resolveConfirm(false)"
      />

      <StatusToast
          :visible="statusState.visible"
          :title="statusState.title"
          :message="statusState.message"
          :detail="statusState.detail"
          :mode="statusState.mode"
          :progress="statusState.progress"
          :variant="statusState.variant"
      />
    </div>
  </div>
</template>

<script>
import AdminPortfolioCard from "@/components/AdminPortfolioCard.vue";
import AdminPortfolioModal from "@/components/AdminPortfolioModal.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import StatusToast from "@/components/StatusToast.vue";
import TechnologyManagerModal from "@/components/TechnologyManagerModal.vue";
import {DeleteRequest, GetRequest, PostRequest, PutRequest} from "@/services/request";
import {inferMediaType} from "@/utils/portfolioMedia";

function createEmptyDraft() {
  return {
    description: '',
    displayOrder: 0,
    id: null,
    media: [],
    subTitle: '',
    technologies: [],
    technologyIds: [],
    title: ''
  };
}

function createStatusState() {
  return {
    detail: '',
    message: '',
    mode: 'spinner',
    progress: 0,
    title: '',
    variant: 'info',
    visible: false
  };
}

export default {
  name: 'AdminView',

  components: {
    AdminPortfolioCard,
    AdminPortfolioModal,
    ConfirmDialog,
    StatusToast
    ,
    TechnologyManagerModal
  },

  data() {
    return {
      checkingSession: true,
      confirmResolver: null,
      confirmState: {
        cancelLabel: 'Annuler',
        confirmLabel: 'Confirmer',
        danger: false,
        message: '',
        title: 'Confirmation',
        visible: false
      },
      creatingTechnology: false,
      draft: createEmptyDraft(),
      dragOriginItems: [],
      draggingItemId: null,
      dragPreviewDirty: false,
      dragSwapLock: '',
      dropPersistRequested: false,
      editorError: '',
      editorOpen: false,
      itemOrderFuture: [],
      itemOrderHistory: [],
      isAuthenticated: false,
      items: [],
      loggingIn: false,
      loginError: '',
      pageError: '',
      password: '',
      reorderDragEndedAt: 0,
      reorderingItems: false,
      saving: false,
      statusHideTimer: null,
      statusState: createStatusState(),
      technologyManagerOpen: false,
      technologies: []
    };
  },

  mounted() {
    this.checkSession();
    window.addEventListener('keydown', this.handleOrderHotkeys);
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.handleOrderHotkeys);
  },

  methods: {
    createEmptyDraft,

    setStatus(nextStatus = {}) {
      if (this.statusHideTimer) {
        clearTimeout(this.statusHideTimer);
        this.statusHideTimer = null;
      }

      this.statusState = {
        ...createStatusState(),
        ...this.statusState,
        ...nextStatus,
        visible: true
      };
    },

    clearStatus(delay = 0) {
      if (this.statusHideTimer) {
        clearTimeout(this.statusHideTimer);
        this.statusHideTimer = null;
      }

      if (delay > 0) {
        this.statusHideTimer = setTimeout(() => {
          this.statusState = createStatusState();
          this.statusHideTimer = null;
        }, delay);
        return;
      }

      this.statusState = createStatusState();
    },

    async confirmAction(options = {}) {
      if (this.confirmResolver) {
        this.resolveConfirm(false);
      }

      this.confirmState = {
        cancelLabel: options.cancelLabel || 'Annuler',
        confirmLabel: options.confirmLabel || 'Confirmer',
        danger: Boolean(options.danger),
        message: options.message || '',
        title: options.title || 'Confirmation',
        visible: true
      };

      return new Promise((resolve) => {
        this.confirmResolver = resolve;
      });
    },

    resolveConfirm(result) {
      if (this.confirmResolver) {
        this.confirmResolver(Boolean(result));
        this.confirmResolver = null;
      }

      this.confirmState.visible = false;
    },

    cloneItem(item) {
      return JSON.parse(JSON.stringify(item));
    },

    snapshotItemOrder(items = this.items) {
      return items.map((item) => ({
        displayOrder: item.displayOrder,
        id: item.id
      }));
    },

    itemOrderEquals(left = [], right = []) {
      if (left.length !== right.length) {
        return false;
      }

      return left.every((entry, index) => entry.id === right[index]?.id && entry.displayOrder === right[index]?.displayOrder);
    },

    async applyItemOrderSnapshot(snapshot = []) {
      const itemMap = new Map(this.items.map((item) => [item.id, this.cloneItem(item)]));
      const reorderedItems = snapshot
          .map((entry, index) => {
            const item = itemMap.get(entry.id);
            if (!item) {
              return null;
            }

            return {
              ...item,
              displayOrder: index
            };
          })
          .filter(Boolean);

      if (reorderedItems.length !== this.items.length) {
        return;
      }

      this.items = reorderedItems;
      this.syncDraftWithItems();
      await this.persistCurrentItemOrder();
    },

    async persistCurrentItemOrder() {
      const items = await PutRequest('admin/portfolio-items/reorder', {
        itemIds: this.items.map((item) => item.id)
      });

      this.items = items;
      this.syncDraftWithItems();
    },

    pushItemOrderHistory(snapshot) {
      const normalizedSnapshot = snapshot.map((entry) => ({...entry}));
      const lastSnapshot = this.itemOrderHistory[this.itemOrderHistory.length - 1];
      if (lastSnapshot && this.itemOrderEquals(lastSnapshot, normalizedSnapshot)) {
        return;
      }

      this.itemOrderHistory.push(normalizedSnapshot);
      if (this.itemOrderHistory.length > 40) {
        this.itemOrderHistory.shift();
      }
    },

    async undoItemOrder() {
      if (!this.isAuthenticated || this.editorOpen || this.reorderingItems || this.itemOrderHistory.length === 0) {
        return;
      }

      const currentSnapshot = this.snapshotItemOrder();
      const previousSnapshot = this.itemOrderHistory.pop();
      if (!previousSnapshot || this.itemOrderEquals(previousSnapshot, currentSnapshot)) {
        return;
      }

      this.itemOrderFuture.push(currentSnapshot);
      this.reorderingItems = true;
      this.pageError = '';

      try {
        await this.applyItemOrderSnapshot(previousSnapshot);
      } catch (error) {
        this.itemOrderHistory.push(previousSnapshot);
        this.itemOrderFuture.pop();
        this.handleRequestError(error, 'Annulation impossible', 'page');
      } finally {
        this.reorderingItems = false;
      }
    },

    async redoItemOrder() {
      if (!this.isAuthenticated || this.editorOpen || this.reorderingItems || this.itemOrderFuture.length === 0) {
        return;
      }

      const currentSnapshot = this.snapshotItemOrder();
      const nextSnapshot = this.itemOrderFuture.pop();
      if (!nextSnapshot || this.itemOrderEquals(nextSnapshot, currentSnapshot)) {
        return;
      }

      this.itemOrderHistory.push(currentSnapshot);
      this.reorderingItems = true;
      this.pageError = '';

      try {
        await this.applyItemOrderSnapshot(nextSnapshot);
      } catch (error) {
        this.itemOrderHistory.pop();
        this.itemOrderFuture.push(nextSnapshot);
        this.handleRequestError(error, 'Rétablissement impossible', 'page');
      } finally {
        this.reorderingItems = false;
      }
    },

    handleOrderHotkeys(event) {
      if (!this.isAuthenticated) {
        return;
      }

      if (!(event.ctrlKey || event.metaKey) || event.altKey) {
        return;
      }

      const target = event.target;
      const tagName = String(target?.tagName || '').toLowerCase();
      const isTypingTarget = target?.isContentEditable || ['input', 'textarea', 'select'].includes(tagName);
      if (isTypingTarget) {
        return;
      }

      const key = String(event.key || '').toLowerCase();
      if (key === 'z' && !event.shiftKey) {
        event.preventDefault();
        this.undoItemOrder();
        return;
      }

      if (key === 'y') {
        event.preventDefault();
        this.redoItemOrder();
      }
    },

    moveArrayEntry(list, fromIndex, toIndex) {
      if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= list.length || toIndex >= list.length) {
        return list;
      }

      const next = [...list];
      const [entry] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, entry);
      return next;
    },

    syncDraftWithItems() {
      if (!this.draft.id) {
        return;
      }

      const currentItem = this.items.find((item) => item.id === this.draft.id);
      if (currentItem) {
        this.draft = this.cloneItem(currentItem);
      }
    },

    setEditorOpen(nextValue) {
      this.editorOpen = Boolean(nextValue);

      if (this.editorOpen) {
        return;
      }

      this.editorError = '';
      this.resetDraftFromSource();
    },

    resetDraftFromSource() {
      if (!this.draft.id) {
        this.draft = this.createEmptyDraft();
        return;
      }

      const currentItem = this.items.find((item) => item.id === this.draft.id);
      this.draft = currentItem ? this.cloneItem(currentItem) : this.createEmptyDraft();
    },

    normalizeDraftMedia() {
      this.draft.media = (this.draft.media || [])
          .map((media, index) => {
            const path = String(media.path || '').trim();
            if (!path) {
              return null;
            }

            return {
              displayOrder: index,
              path,
              type: inferMediaType(path)
            };
          })
          .filter(Boolean);
    },

    async checkSession() {
      try {
        const data = await GetRequest('auth/session');
        this.isAuthenticated = Boolean(data.authenticated);

        if (this.isAuthenticated) {
          await this.loadAdminData();
        }
      } catch (_error) {
        this.pageError = 'Session indisponible';
      } finally {
        this.checkingSession = false;
      }
    },

    async login() {
      this.loggingIn = true;
      this.loginError = '';

      try {
        await PostRequest('auth/login', {password: this.password});
        this.password = '';
        this.isAuthenticated = true;
        await this.loadAdminData();
      } catch (error) {
        this.loginError = error?.response?.data?.error || 'Connexion refusée';
      } finally {
        this.loggingIn = false;
      }
    },

    async loadAdminData() {
      this.pageError = '';

      try {
        const [items, technologies] = await Promise.all([
          GetRequest('admin/portfolio-items'),
          GetRequest('admin/technologies')
        ]);

        this.items = items;
        this.itemOrderHistory = [];
        this.itemOrderFuture = [];
        this.technologies = technologies;
        this.syncDraftWithItems();
      } catch (error) {
        this.handleRequestError(error, 'Portfolio indisponible', 'page');
      }
    },

    selectItem(itemId) {
      if (Date.now() - this.reorderDragEndedAt < 180) {
        return;
      }

      const item = this.items.find((entry) => entry.id === itemId);
      if (!item) {
        return;
      }

      this.editorError = '';
      this.draft = this.cloneItem(item);
      this.editorOpen = true;
    },

    onItemDragStart(itemId, event) {
      if (this.reorderingItems) {
        return;
      }

      if (event?.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.dropEffect = 'move';
      }

      this.draggingItemId = itemId;
      this.dragOriginItems = this.items.map((item) => this.cloneItem(item));
      this.dragPreviewDirty = false;
      this.dropPersistRequested = false;
    },

    shouldSwapItem(targetItemId, event) {
      if (!event?.currentTarget) {
        return true;
      }

      const fromIndex = this.items.findIndex((item) => item.id === this.draggingItemId);
      const toIndex = this.items.findIndex((item) => item.id === targetItemId);
      if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
        return false;
      }

      const rect = event.currentTarget.getBoundingClientRect();
      const multiColumn = window.innerWidth > 700;
      const progress = multiColumn
          ? (event.clientX - rect.left) / Math.max(rect.width, 1)
          : (event.clientY - rect.top) / Math.max(rect.height, 1);

      if (fromIndex < toIndex) {
        return progress >= 0.66;
      }

      return progress <= 0.34;
    },

    onItemDragOver(targetItemId, event) {
      if (this.draggingItemId === null || this.reorderingItems) {
        return;
      }

      const fromIndex = this.items.findIndex((item) => item.id === this.draggingItemId);
      const toIndex = this.items.findIndex((item) => item.id === targetItemId);

      if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
        return;
      }

      const swapKey = `${this.draggingItemId}:${targetItemId}:${fromIndex < toIndex ? 'after' : 'before'}`;
      if (this.dragSwapLock === swapKey && !this.shouldSwapItem(targetItemId, event)) {
        this.dragSwapLock = '';
      }

      if (!this.shouldSwapItem(targetItemId, event) || this.dragSwapLock === swapKey) {
        return;
      }

      this.items = this.moveArrayEntry(this.items, fromIndex, toIndex).map((item, index) => ({
        ...item,
        displayOrder: index
      }));
      this.dragSwapLock = swapKey;
      this.dragPreviewDirty = true;
      this.syncDraftWithItems();
    },

    async onItemDrop() {
      if (this.draggingItemId === null || this.reorderingItems) {
        return;
      }

      this.dropPersistRequested = true;

      if (!this.dragPreviewDirty) {
        return;
      }

      const previousItems = this.dragOriginItems.map((item) => this.cloneItem(item));
      this.reorderingItems = true;
      this.pageError = '';

      try {
        const previousSnapshot = this.snapshotItemOrder(previousItems);
        this.pushItemOrderHistory(previousSnapshot);
        this.itemOrderFuture = [];
        await this.persistCurrentItemOrder();
      } catch (error) {
        this.items = previousItems;
        this.syncDraftWithItems();
        this.handleRequestError(error, 'Réorganisation impossible', 'page');
      } finally {
        this.reorderingItems = false;
      }
    },

    onItemDragEnd() {
      if (!this.dropPersistRequested && this.dragPreviewDirty && this.dragOriginItems.length) {
        this.items = this.dragOriginItems.map((item) => this.cloneItem(item));
        this.syncDraftWithItems();
      }

      this.reorderDragEndedAt = Date.now();
      this.draggingItemId = null;
      this.dragOriginItems = [];
      this.dragSwapLock = '';
      this.dragPreviewDirty = false;
      this.dropPersistRequested = false;
    },

    createDraft() {
      this.editorError = '';
      this.draft = this.createEmptyDraft();
      this.draft.displayOrder = 0;
      this.editorOpen = true;
    },

    async saveItem(nextDraft = this.draft) {
      this.saving = true;
      this.editorError = '';
      const isCreating = !this.draft.id;
      this.draft = this.cloneItem(nextDraft);
      this.normalizeDraftMedia();
      this.setStatus({
        message: isCreating ? 'Création en cours' : 'Mise à jour en cours',
        mode: 'spinner',
        title: isCreating ? 'Ajout de l’item' : 'Enregistrement',
        variant: 'info'
      });

      const payload = {
        description: this.draft.description,
        displayOrder: Number(this.draft.displayOrder || 0),
        media: this.draft.media,
        subTitle: this.draft.subTitle,
        technologyIds: [...(this.draft.technologyIds || [])],
        title: this.draft.title
      };

      try {
        const savedItem = this.draft.id
            ? await PutRequest(`admin/portfolio-items/${this.draft.id}`, payload)
            : await PostRequest('admin/portfolio-items', payload);

        await this.loadAdminData();

        if (isCreating) {
          this.editorOpen = false;
          this.draft = this.createEmptyDraft();
          this.setStatus({
            message: 'L’item a été ajouté en tête de liste',
            mode: 'info',
            title: 'Création terminée',
            variant: 'success'
          });
          this.clearStatus(1500);
          return;
        }

        if (savedItem?.id) {
          const currentItem = this.items.find((item) => item.id === savedItem.id);
          if (currentItem) {
            this.draft = this.cloneItem(currentItem);
          }
        }
        this.setStatus({
          message: 'Les changements ont été enregistrés',
          mode: 'info',
          title: 'Enregistrement terminé',
          variant: 'success'
        });
        this.clearStatus(1400);
      } catch (error) {
        this.setStatus({
          message: error?.response?.data?.error || error?.response?.data?.detail || 'Enregistrement impossible',
          mode: 'spinner',
          title: 'Erreur',
          variant: 'error'
        });
        this.clearStatus(2200);
        this.handleRequestError(error, 'Enregistrement impossible', 'editor');
      } finally {
        this.saving = false;
      }
    },

    async deleteItem(itemId = this.draft.id) {
      if (!itemId) {
        return;
      }

      const confirmed = await this.confirmAction({
        confirmLabel: 'Supprimer',
        danger: true,
        message: 'Cet item du portfolio sera supprimé définitivement.',
        title: 'Supprimer l’item'
      });
      if (!confirmed) {
        return;
      }

      this.saving = true;
      this.editorError = '';
      this.setStatus({
        message: 'Suppression en cours',
        mode: 'spinner',
        title: 'Suppression',
        variant: 'info'
      });

      try {
        await DeleteRequest(`admin/portfolio-items/${itemId}`);
        this.editorOpen = false;
        this.draft = this.createEmptyDraft();
        await this.loadAdminData();
        this.setStatus({
          message: 'L’item a été supprimé',
          mode: 'info',
          title: 'Suppression terminée',
          variant: 'success'
        });
        this.clearStatus(1400);
      } catch (error) {
        this.setStatus({
          message: error?.response?.data?.error || error?.response?.data?.detail || 'Suppression impossible',
          mode: 'spinner',
          title: 'Erreur',
          variant: 'error'
        });
        this.clearStatus(2200);
        this.handleRequestError(error, 'Suppression impossible', 'editor');
      } finally {
        this.saving = false;
      }
    },

    async createTechnology(name) {
      const normalizedName = String(name || '').trim();
      if (!normalizedName) {
        return null;
      }

      this.creatingTechnology = true;

      try {
        const technology = await PostRequest('admin/technologies', {name: normalizedName});
        this.technologies = [...this.technologies, technology].sort((left, right) => left.name.localeCompare(right.name));
        return technology;
      } catch (error) {
        this.handleRequestError(error, 'Création impossible', 'editor');
        throw error;
      } finally {
        this.creatingTechnology = false;
      }
    },

    async updateTechnology(technologyId, name) {
      const normalizedName = String(name || '').trim();
      if (!normalizedName) {
        return null;
      }

      try {
        const technology = await PutRequest(`admin/technologies/${technologyId}`, {name: normalizedName});
        this.technologies = this.technologies
            .map((entry) => (entry.id === technology.id ? technology : entry))
            .sort((left, right) => left.name.localeCompare(right.name));
        return technology;
      } catch (error) {
        this.setStatus({
          message: error?.response?.data?.error || error?.response?.data?.detail || 'Mise à jour impossible',
          mode: 'spinner',
          title: 'Erreur',
          variant: 'error'
        });
        this.clearStatus(2200);
        throw error;
      }
    },

    async deleteTechnology(technologyId) {
      const normalizedId = Number(technologyId);
      if (!normalizedId) {
        return;
      }

      const confirmed = await this.confirmAction({
        confirmLabel: 'Supprimer',
        danger: true,
        message: 'Cette technologie sera retirée de la base si elle n’est utilisée nulle part.',
        title: 'Supprimer la technologie'
      });
      if (!confirmed) {
        return;
      }

      try {
        await DeleteRequest(`admin/technologies/${normalizedId}`);
        this.technologies = this.technologies.filter((technology) => technology.id !== normalizedId);
        this.items = this.items.map((item) => ({
          ...item,
          technologies: (item.technologies || []).filter((technology) => technology.id !== normalizedId),
          technologyIds: (item.technologyIds || []).filter((id) => id !== normalizedId)
        }));
        this.draft = {
          ...this.draft,
          technologyIds: (this.draft.technologyIds || []).filter((id) => id !== normalizedId)
        };
        this.setStatus({
          message: 'La technologie a été supprimée',
          mode: 'info',
          title: 'Suppression terminée',
          variant: 'success'
        });
        this.clearStatus(1400);
      } catch (error) {
        this.setStatus({
          message: error?.response?.data?.error || error?.response?.data?.detail || 'Suppression impossible',
          mode: 'spinner',
          title: 'Erreur',
          variant: 'error'
        });
        this.clearStatus(2200);
        throw error;
      }
    },

    handleRequestError(error, fallbackMessage, target = 'page') {
      if (error?.response?.status === 401) {
        this.isAuthenticated = false;
        this.editorOpen = false;
        this.loginError = 'Session expirée';
        return;
      }

      const message = error?.response?.data?.error || error?.response?.data?.detail || fallbackMessage;

      if (target === 'editor') {
        this.editorError = message;
        return;
      }

      this.pageError = message;
    }
  }
}
</script>

<style scoped>
.admin-view {
  min-height: 100vh;
}

.login-card {
  background: #323336;
  border-radius: 24px;
  box-sizing: border-box;
}

.loading-shell,
.login-shell {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-spinner {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: 6px solid rgba(255, 255, 255, 0.12);
  border-top-color: #00f0ff;
  animation: admin-spin 0.82s linear infinite;
}

.login-card {
  width: min(360px, calc(100vw - 2rem));
  padding: 1.5rem;
  display: grid;
  gap: 0.75rem;
}

.login-card input,
.login-card button {
  width: 100%;
  box-sizing: border-box;
  border-radius: 14px;
  padding: 0.85rem 1rem;
}

.login-card input {
  border: 1px solid #50545d;
  background: #252629;
  color: white;
}

.login-card button {
  border: 1px solid #00f0ff;
  cursor: pointer;
  background: rgba(0, 240, 255, 0.12);
  color: #00f0ff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.admin-shell {
  display: grid;
  gap: 1rem;
  max-width: 1200px;
  margin: 0 auto 2rem;
  padding-top: 0.75rem;
}

.icon-action {
  position: fixed;
  z-index: 30;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #50545d;
  cursor: pointer;
  color: white;
  background: #252629;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.24);
  transition: transform 0.18s ease, border-color 0.18s ease, color 0.18s ease, box-shadow 0.18s ease;
}

.icon-action svg {
  width: 1.35rem;
  height: 1.35rem;
  fill: currentColor;
}

.icon-action:hover {
  transform: translateY(-2px);
  border-color: #00f0ff;
  color: #00f0ff;
  box-shadow: 0 0 20px rgba(0, 240, 255, 0.25);
}
.icon-action--create {
  right: 24px;
  bottom: 24px;
  width: 64px;
  height: 64px;
  border-radius: 999px;
  background: rgba(0, 240, 255, 0.12);
  border-color: #00f0ff;
  color: #00f0ff;
}

.icon-action--technologies {
  right: 24px;
  bottom: 100px;
  width: 56px;
  height: 56px;
  border-radius: 999px;
}

.portfolio-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(3, 1fr);
  align-items: stretch;
  perspective: 1200px;
}
.portfolio-grid-move {
  transition: transform 0.24s cubic-bezier(0.2, 0.9, 0.2, 1);
}

.error-text {
  color: #ff8a8a;
  margin: 0;
}

.page-error {
  padding-inline: 0.25rem;
}

@media (max-width: 1100px) {
  .portfolio-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 700px) {
  .portfolio-grid {
    grid-template-columns: 1fr;
  }

  .icon-action--create {
    right: 16px;
    bottom: 16px;
    width: 58px;
    height: 58px;
  }

  .icon-action--technologies {
    right: 17px;
    bottom: 86px;
    width: 50px;
    height: 50px;
  }
}

@keyframes admin-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
